import { useEffect, useMemo, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { Add } from '@mesh.ai/rabbit';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import Carousel from 'components/Carousel';
import { useQuery } from 'components/hooks/useQuery';
import { format } from 'date-fns';
import { capitalize } from 'lodash';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { closeSidebar, openSidebar } from 'reducers/sidebarNav-slice';
import { RRule } from 'rrule';
import { OneOnOneCompletion } from 'screens/oneOnOneRabbit/Insights/features/Graphs/OneOnOneCompletion/OneOnOneCompletion';
import ThemeDistribution from 'screens/oneOnOneRabbit/Insights/features/Graphs/ThemeDistribution/ThemeDistribution';
import OneOnOneHeading from 'screens/oneOnOneRabbit/Insights/features/OneOnOneHeading';
import {
  setFilterTextOneOnOneInsightsFilters,
  useOneOnOneInsightsFiltersData,
} from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';
import { openCreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer/slice/CreateOneOnOneDrawerSlice';
import ActiveOneOnOneTable from 'screens/oneOnOneRabbit/landingPage/features/LandingPageTable/ActiveOneOnOneTable';
import ArchivedOneOnOneTable from 'screens/oneOnOneRabbit/landingPage/features/LandingPageTable/ArchivedOneOnOneTable';
import { useOneOnOneLandingPageDetails } from 'screens/oneOnOneRabbit/landingPage/hooks/dataHooks';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Plus, Search } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';
import { sendClickedAddNewOnOneOnOne, sendPageViewEvent } from 'constants/mixpanelHelper';
import { isNetchexLogin } from 'constants/netChexConstants';

import { AgGridSearch } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridActionButton } from 'uiToolkitV1/AgGridWrapperV2/components/ActionButton/AgGridActionButton';
import { Button } from 'uiToolkitV1/Button';
import { ChakraStyleInput } from 'uiToolkitV1/InputV2/ChakraStyleInput';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

const StyledCarousel = styled(Carousel)`
  padding: 8px 0px;
  margin-bottom: 4px;
`;

const ONE_ON_ONE_FILTERS_ARRAY = [
  { sectionId: 'ACTIVE', sectionKeyName: 'Active', beKey: 'activeCount', mixpanelKey: 'Me-1:1-manage' },
  { sectionId: 'ARCHIVED', sectionKeyName: 'Archived', beKey: 'archivedCount', mixpanelKey: 'Me-1:1-archived' },
];

const TABLE_TYPES_COMP = {
  ACTIVE: ActiveOneOnOneTable,
  ARCHIVED: ArchivedOneOnOneTable,
};

const OneOnOneIntentLandingPage = () => {
  const { oneOnOneType } = useQuery();
  const history = useHistory();
  const location = useLocation();
  const { dateRange, filterText } = useOneOnOneInsightsFiltersData();

  const modules = useRouteToNavLevels();
  const isShowReportees = !checkNewNavEnabled() || modules?.l1Module === 'team';

  const dispatch = useDispatch();

  const gridRef = useRef(null);

  // here we are passing selectedCohortId null because on my 1-1 page it is expecting null
  const { data: oooData, isLoading: isLoadingMetricsData } = useOneOnOneLandingPageDetails({
    isArchivedList: false,
    selectedCohortId: null,
    dateRange,
    enabled: oneOnOneType === 'ACTIVE',
    isShowReportees,
  });

  // Filtering data for manager-related entries

  const extractedDataForManager = useMemo(() => {
    const filteredData = oooData?.data.filter((entry) => entry?.employee?.value?.relationType === 'MANAGER');
    return filteredData?.map((entry) => {
      const { latestInstanceId } = entry?.nextOneOnOne?.value || {};
      const { oneOnOneMasterId: employeeOneOnOneMasterId, isOneOnOneSetup } = entry.employee.value;
      const { value: completionPercentageValue } = entry.completionPercentage || {};
      const { value: averageGapValue } = entry.averageGap || {};
      const { recurrenceDetails, nextInstanceTS } = entry?.nextOneOnOne?.value || {};
      const oneOnoneSetupDate = nextInstanceTS ? format(new Date(nextInstanceTS * 1000), 'PPp') : 'NA';
      const recurrenceData = RRule.fromString(recurrenceDetails || '');
      const oneOnOneSetupDetails = recurrenceData.toText();
      const dayCapitalizedRRuleString = oneOnOneSetupDetails
        ? `${oneOnOneSetupDetails
            .split(' ')
            .slice(0, -1)
            .map((part) => capitalize(part))
            .join(' ')} ${capitalize(oneOnOneSetupDetails.split(' ').pop())}`
        : '';
      return {
        oneOnOneMasterId: employeeOneOnOneMasterId,
        latestInstanceId,
        completionPercentageValue,
        averageGapValue,
        dayCapitalizedRRuleString,
        oneOnoneSetupDate,
        isOneOnOneSetup,
      };
    });
  }, [oooData]);

  const metricsCardData = useMemo(
    () =>
      extractedDataForManager
        ?.map((data) => [
          {
            title: 'Avg. Completion %',
            value:
              data.completionPercentageValue !== null && data.completionPercentageValue !== undefined
                ? `${data.completionPercentageValue}%`
                : 'NA',
          },
          {
            title: 'Avg. Time Gap between 1:1s',
            value:
              data.averageGapValue !== null && data.averageGapValue !== undefined
                ? `${Math.round(data.averageGapValue)} ${Number(data.averageGapValue) > 1 ? 'days' : 'day'}`
                : 'NA',
          },
        ])
        .flat(),
    [extractedDataForManager]
  );

  useEffect(() => {
    if (!oneOnOneType) {
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('oneOnOneType', ONE_ON_ONE_FILTERS_ARRAY[0].sectionId);
      history.replace({
        search: queryParams.toString(),
      });
    }
  }, [history, location.search, oneOnOneType]);

  const onTextFilterChangeHandler = ({ value }) => {
    dispatch(
      setFilterTextOneOnOneInsightsFilters({
        filterText: value,
      })
    );
  };

  const openCreateOneOnOneSidepanel = () => {
    sendClickedAddNewOnOneOnOne();
    dispatch(
      // @ts-expect-error not properly typed yet
      openCreateOneOnOneDrawer({
        excludedParticipantIds: [],
      })
    );
  };

  useEffect(() => {
    dispatch(openSidebar());
    sendPageViewEvent({
      eventProperties: {
        page: ONE_ON_ONE_FILTERS_ARRAY.find((item) => item.sectionId === oneOnOneType)?.mixpanelKey,
      },
    });
  }, [dispatch, ONE_ON_ONE_FILTERS_ARRAY.find((item) => item.sectionId === oneOnOneType)?.mixpanelKey]);

  useEffect(() => {
    if (isMobileView()) {
      dispatch(closeSidebar());
    }
  }, [dispatch]);

  const TableComp = TABLE_TYPES_COMP?.[oneOnOneType] ?? (() => null);

  if (isMobileView()) {
    return (
      <>
        {!isNetchexLogin && <OneOnOneHeading />}
        <PaperWithShadow p="10px 12px" mt={3} mx={!checkNewNavEnabled() ? 3 : 0}>
          <Flex alignItems="center">
            <Box width="100%">
              <Flex gap={4}>
                <ChakraStyleInput
                  leftComponent={<Search />}
                  placeholder="Search"
                  value={filterText}
                  onChange={(e) => onTextFilterChangeHandler({ value: e.target.value })}
                />
                <Button StartIcon={Plus} onClick={openCreateOneOnOneSidepanel}>
                  Add New
                </Button>
              </Flex>
            </Box>
          </Flex>

          <TableComp />
        </PaperWithShadow>
      </>
    );
  }

  let noOfMetricCardsToShow = 2;
  if (metricsCardData) {
    noOfMetricCardsToShow = Math.max(2, Math.min(metricsCardData.length, 4));
  }

  return (
    <>
      {!checkNewNavEnabled() && <OneOnOneHeading />}
      <Box p={!checkNewNavEnabled() ? 4 : 0}>
        <Flex mb={4} gap={4} h="348px">
          <PaperWithShadow w="50%" p={0} h="100%">
            <OneOnOneCompletion isManagePage />
          </PaperWithShadow>
          <PaperWithShadow w="50%" p={0} h="100%">
            <ThemeDistribution />
          </PaperWithShadow>
        </Flex>
        {checkNewNavEnabled() && oneOnOneType === 'ACTIVE' && (
          <MetricCards
            height="100%"
            columns={`repeat(${noOfMetricCardsToShow}, 1fr)`}
            cardTitle="Metrics"
            cards={metricsCardData}
            //  module={MODULE_KEYS_METRIC_CARDS_DEER.GOAL}
            //  gridStatePersistenceKey={targetMetricCardTablePersistenceKey}
          />
          // <StyledCarousel slidesToShow={3} slidesToScroll={1} slideMargin="10px" stretchChildren>
          //   {!isLoadingMetricsData &&
          //     metricsCardData.map((metricsData, index) => (
          //       // eslint-disable-next-line react/no-array-index-key
          //       <MetricCard key={index}>
          //         <MetricCardText>{metricsData?.title}</MetricCardText>
          //         <Metric>{metricsData?.value}</Metric>
          //       </MetricCard>
          //     ))}
          // </StyledCarousel>
        )}

        <PaperWithShadow p="3" mt={3}>
          <Box>
            <Flex justifyContent="space-between" alignItems="center" mr="auto">
              <AgGridSearch gridRef={gridRef} placeholder="Search" cohortId={oneOnOneType} />
              <AgGridActionButton Icon={Add} onClick={openCreateOneOnOneSidepanel} label="Add New" />
            </Flex>
          </Box>

          <TableComp gridRef={gridRef} />
        </PaperWithShadow>
      </Box>
    </>
  );
};

export { OneOnOneIntentLandingPage };
